import React from "react";
import type { BuilderItem } from "../../types/gangsheet";
import {
  SHEET_WIDTH_IN,
  clampCenterToSheet,
  getDpi,
  getDpiTone,
  normalizeAngle,
} from "../../utils/gangsheetMath";
import {
  controlLabelStyle,
  DataTile,
  panelStyle,
  SectionTitle,
  subtlePanelStyle,
} from "./GangsheetUI";

type Props = {
  selectedItem: BuilderItem | null;
  sheetLengthIn: number;
  updateItem: (id: string, updater: (item: BuilderItem) => BuilderItem) => void;
};

export default function GangsheetSelectionPanel({
  selectedItem,
  sheetLengthIn,
  updateItem,
}: Props) {
  return (
    <div style={{ ...panelStyle, padding: 14, display: "grid", gap: 14 }}>
      <SectionTitle
        title="Selection & Artwork"
        subtitle="Edit selected artwork and manage your loaded pieces."
      />

      {selectedItem ? (
        <div style={{ ...subtlePanelStyle, padding: 14, display: "grid", gap: 12 }}>
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 900,
                color: "#eff6ff",
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              {selectedItem.name}
            </div>
            <div style={{ color: "#93c5fd", marginTop: 4 }}>
              {selectedItem.naturalWidth} × {selectedItem.naturalHeight}px
            </div>
          </div>

          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
            <DataTile
              label="Placed Size"
              value={`${selectedItem.widthIn.toFixed(2)}" × ${selectedItem.heightIn.toFixed(2)}"`}
            />
            <DataTile
              label="Rotation"
              value={`${Math.round(normalizeAngle(selectedItem.rotation))}°`}
            />
          </div>

          <div
            style={{
              padding: 12,
              borderRadius: 14,
              background: getDpiTone(getDpi(selectedItem)).bg,
              border: `1px solid ${getDpiTone(getDpi(selectedItem)).border}`,
            }}
          >
            <div style={{ ...controlLabelStyle, marginBottom: 6 }}>
              Effective DPI
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 900,
                color: getDpiTone(getDpi(selectedItem)).color,
              }}
            >
              {getDpi(selectedItem)} DPI
            </div>
            <div
              style={{
                marginTop: 4,
                color: "#cbd5e1",
                fontSize: 13,
                lineHeight: 1.4,
              }}
            >
              Status: {getDpiTone(getDpi(selectedItem)).label}
            </div>
          </div>

          <label style={{ display: "grid", gap: 8 }}>
            <span style={controlLabelStyle}>
              Width: {selectedItem.widthIn.toFixed(2)}"
            </span>
            <input
              type="range"
              min={0.5}
              max={22}
              step={0.05}
              value={selectedItem.widthIn}
              onChange={(e) => {
                const nextWidth = Number(e.target.value);
                const ratio =
                  selectedItem.heightIn / Math.max(selectedItem.widthIn, 0.001);
                const nextHeight = nextWidth * ratio;

                updateItem(selectedItem.id, (item) =>
                  clampCenterToSheet(
                    {
                      ...item,
                      widthIn: nextWidth,
                      heightIn: nextHeight,
                    },
                    sheetLengthIn
                  )
                );
              }}
            />
          </label>

          <label style={{ display: "grid", gap: 8 }}>
            <span style={controlLabelStyle}>
              Rotation: {Math.round(normalizeAngle(selectedItem.rotation))}°
            </span>
            <input
              type="range"
              min={0}
              max={360}
              step={1}
              value={Math.round(normalizeAngle(selectedItem.rotation))}
              onChange={(e) => {
                const nextRotation = Number(e.target.value);

                updateItem(selectedItem.id, (item) =>
                  clampCenterToSheet(
                    {
                      ...item,
                      rotation: nextRotation,
                    },
                    sheetLengthIn
                  )
                );
              }}
            />
          </label>

          <label style={{ display: "grid", gap: 8 }}>
            <span style={controlLabelStyle}>
              X Position: {selectedItem.centerX.toFixed(2)}"
            </span>
            <input
              type="range"
              min={0}
              max={SHEET_WIDTH_IN}
              step={0.01}
              value={selectedItem.centerX}
              onChange={(e) => {
                const nextCenterX = Number(e.target.value);

                updateItem(selectedItem.id, (item) =>
                  clampCenterToSheet(
                    {
                      ...item,
                      centerX: nextCenterX,
                    },
                    sheetLengthIn
                  )
                );
              }}
            />
          </label>

          <label style={{ display: "grid", gap: 8 }}>
            <span style={controlLabelStyle}>
              Y Position: {selectedItem.centerY.toFixed(2)}"
            </span>
            <input
              type="range"
              min={0}
              max={sheetLengthIn}
              step={0.01}
              value={selectedItem.centerY}
              onChange={(e) => {
                const nextCenterY = Number(e.target.value);

                updateItem(selectedItem.id, (item) =>
                  clampCenterToSheet(
                    {
                      ...item,
                      centerY: nextCenterY,
                    },
                    sheetLengthIn
                  )
                );
              }}
            />
          </label>
        </div>
      ) : (
        <div
          style={{
            ...subtlePanelStyle,
            padding: 16,
            color: "#93c5fd",
            lineHeight: 1.5,
          }}
        >
          Select artwork on the canvas to edit size, rotation, and position.
        </div>
      )}
    </div>
  );
}